import Component from '../abstracts/Component';

export default class CollapseComponent extends Component {

  get COMPONENTNAME() {
    return 'CollapseComponent';
  }

  get SELECTORS() {
    return {
      trigger: '[data-trigger]',
      tab: '[data-tab]',
    };
  }

  get CLASSES() {
    return {
      collapsed: 'collapsed'
    }
  }

  constructor(compEl) {
    super(compEl);
  }

  readDOM() {
    return {
      triggers: this.$component.querySelectorAll(this.SELECTORS.trigger),
      tabs: this.$component.querySelectorAll(this.SELECTORS.tab),
      currentOpenTrigger: null,
    };
  }

  //get all the tabs and set the height
  init() {
    if(this.dom.tabs) {
      this.dom.tabs.forEach(tab => {
        tab.style.maxHeight = this.getHiddenHeight(tab) + "px";
      });
    }
    if(this.dom.triggers) {
      this.dom.triggers.forEach(trigger => {
        //get trigger-related tab
        const tab = trigger.parentNode.querySelector(this.SELECTORS.tab);
        // Check if trigger is actually interactive (has pointer-events enabled)
        const triggerStyle = window.getComputedStyle(trigger);
        const isInteractive = triggerStyle.pointerEvents !== 'none';
        
        if(trigger.dataset.trigger === "start-open") {
          trigger.dataset.trigger = "on";
          tab.setAttribute('aria-hidden', 'false');
          // Only set inert if trigger is interactive (on mobile)
          if(isInteractive) {
            tab.inert = false;
          }
          this.dom.currentOpenTrigger = trigger;
        } else {
          tab.classList.add(this.CLASSES.collapsed);
          trigger.classList.add(this.CLASSES.collapsed);
          trigger.dataset.trigger = "off";
          // Only set aria-hidden and inert if trigger is interactive (on mobile)
          if(isInteractive) {
            tab.setAttribute('aria-hidden', 'true');
            tab.inert = true;
          } else {
            // On desktop where trigger has pointer-events: none, keep tab accessible
            tab.setAttribute('aria-hidden', 'false');
            tab.inert = false;
          }
        }
      });
    }
    // remove display: none AFTER collapsed class is set to avoid flickering
    if(this.dom.tabs) {
      this.dom.tabs.forEach(tab => {
        tab.style.display = 'block';
      });
    }
  }

  refreshHeight(event, payload) {
    if (!payload) return;
    // Determine the DOM element to use as reference
    const sourceEl = payload.btn || payload.extraDiv;
    if (!sourceEl) return;
    // Ensure we select the .tab inside this specific component
    const tabEl = sourceEl.closest(this.SELECTORS.tab);
    const firstChild = tabEl?.children[0];

    if (!tabEl || !firstChild || firstChild.offsetHeight === 0) return;
    // Defer style update to after layout changes
    requestAnimationFrame(() => {
      const newHeight = firstChild.offsetHeight + 'px';
      //tabEl.style.height = newHeight;
      tabEl.style.maxHeight = newHeight;
    });
  }

  //handle click on trigger element
  handleCollapse() {
    if(this.dom.triggers) {
      this.dom.triggers.forEach(trigger => {
        const handleToggle = () => {
          //get trigger-related tab
          const tab = trigger.parentNode.querySelector(this.SELECTORS.tab);
          // Get the link element inside trigger for aria-expanded
          const triggerLink = trigger.querySelector('a[role="button"]');
          
          if(this.dom.currentOpenTrigger === trigger) {
            // click on the same trigger to close the section
            tab.style.maxHeight = "0px";
            tab.classList.add(this.CLASSES.collapsed);
            trigger.classList.add(this.CLASSES.collapsed);
            trigger.dataset.trigger = "off";
            if(triggerLink) triggerLink.setAttribute('aria-expanded', 'false');
            if(trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
            tab.setAttribute('aria-hidden', 'true');
            tab.inert = true;
            // Announce state change
            const statusMessage = trigger.querySelector('[data-status-message]');
            const showLabel = trigger.querySelector('.show-label');
            if(statusMessage && showLabel) {
              statusMessage.textContent = showLabel.textContent + ', link';
            }
            this.dom.currentOpenTrigger = null;
          } else if(this.dom.currentOpenTrigger != null) {
            //another tab is currently open
            //find current open tab to close it
            const currentOpenTab = this.dom.currentOpenTrigger.parentNode.querySelector(this.SELECTORS.tab);
            const currentOpenLink = this.dom.currentOpenTrigger.querySelector('a[role="button"]');
            currentOpenTab.style.maxHeight = "0px";
            currentOpenTab.classList.add(this.CLASSES.collapsed);
            this.dom.currentOpenTrigger.classList.add(this.CLASSES.collapsed);
            this.dom.currentOpenTrigger.dataset.trigger = "off";
            if(currentOpenLink) currentOpenLink.setAttribute('aria-expanded', 'false');
            if(this.dom.currentOpenTrigger.hasAttribute('aria-expanded')) this.dom.currentOpenTrigger.setAttribute('aria-expanded', 'false');
            currentOpenTab.setAttribute('aria-hidden', 'true');
            currentOpenTab.inert = true;
            tab.style.maxHeight = tab.scrollHeight + "px";
            tab.classList.remove(this.CLASSES.collapsed);
            trigger.classList.remove(this.CLASSES.collapsed);
            trigger.dataset.trigger = "on";
            if(triggerLink) triggerLink.setAttribute('aria-expanded', 'true');
            if(trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');
            tab.setAttribute('aria-hidden', 'false');
            tab.inert = false;
            // Announce state change
            const statusMessage = trigger.querySelector('[data-status-message]');
            const hideLabel = trigger.querySelector('.hide-label');
            if(statusMessage && hideLabel) {
              statusMessage.textContent = hideLabel.textContent + ', link';
            }
            this.dom.currentOpenTrigger = trigger;
          } else {
            //is the first trigger clicked
            tab.classList.remove(this.CLASSES.collapsed);
            tab.style.maxHeight = tab.scrollHeight + "px";
            trigger.classList.remove(this.CLASSES.collapsed);
            trigger.dataset.trigger = "on";
            if(triggerLink) triggerLink.setAttribute('aria-expanded', 'true');
            if(trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');
            tab.setAttribute('aria-hidden', 'false');
            tab.inert = false;
            // Announce state change
            const statusMessage = trigger.querySelector('[data-status-message]');
            const hideLabel = trigger.querySelector('.hide-label');
            if(statusMessage && hideLabel) {
              statusMessage.textContent = hideLabel.textContent + ', link';
            }
            this.dom.currentOpenTrigger = trigger;
          }
        };

        trigger.addEventListener("click", handleToggle);
        
        // Add keyboard support for Enter and Space keys
        trigger.addEventListener("keydown", (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        });
      });
    }
  }

  // get height of hidden elements, avoids seeing element open during page load
  getHiddenHeight(el) {
    if(!el?.cloneNode) {
      return null;
    }

    const clone = el.cloneNode(true);
    const parentWidth = el.parentNode.offsetWidth;
    Object.assign(clone.style, {
      overflow: 'visible',
      height: 'auto',
      width: parentWidth + 'px',
      position: 'absolute',
      opacity: '0',
      visibility: 'hidden',
      display: 'block',
    });

    el.after(clone);
    const height = clone.offsetHeight;

    clone.remove();

    return height;
  }

  bindEvents() {
    this.handleCollapse();
    this.$on(this.$customEvents.COLLAPSE_EVENTS.update,({event, payload}) => {
      this.refreshHeight(event, payload);
    });
    window.addEventListener('resize', () => {
      if (this.dom.currentOpenTrigger) {
        const tab = this.dom.currentOpenTrigger.parentNode.querySelector(this.SELECTORS.tab);
        tab.style.maxHeight = tab.scrollHeight + "px";
      }
    });
  }

  render() {
    super.render();
    this.log('Rendering...');
    this.dom = this.readDOM();
    this.init();
    this.bindEvents();
  }
}
